import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { useGetPromoQuotaQuery } from '../../../../../../redux/api/fymAPI';
import type {
  CatalogItem,
  GetReleaseKitResponse,
  ReleasePlan,
  ReleaseReelsPlan,
} from '../../../../../../redux/api/fymAPI.types';
import { Panel } from '../../../../components/primitives/Panel';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { useVideoCredits } from '../../../credits/useVideoCredits';
import type { SerializedSocialChannel, SocialPlatform } from '../../../types';
import { useIsNarrowScreen } from '../../../work/useIsNarrowScreen';
import { LaunchQuestionsDialog } from '../rollout/launch/LaunchQuestionsDialog';
import { launchMessages } from '../rollout/launch/launchMessages';
import { LaunchSummary } from '../rollout/launch/LaunchSummary';
import { ScheduleAllUpsell } from '../rollout/launch/ScheduleAllUpsell';
import { RolloutContext, type RolloutContextValue } from '../rollout/RolloutContext';
import { fitDrafts, planDrafts, planSteps } from '../rollout/rolloutModel';
import { StepSheet } from '../rollout/StepSheet';
import { useRolloutActions } from '../rollout/useRolloutActions';
import { EndReelsPlanDialog } from './EndReelsPlanDialog';
import { ReelsPlanChannelsLine } from './ReelsPlanChannelsLine';
import { ReelsPlanHead } from './ReelsPlanHead';
import { reelsPlanMessages } from './reelsPlanMessages';
import { reelsPlanEndCounts } from './reelsPlanModel';
import { ReelsPlanSteps } from './ReelsPlanSteps';
import { useReelsPlan } from './useReelsPlan';
/* eslint-disable css-modules/no-unused-class -- each part uses its own slice of the two stylesheets */
import rolloutStyles from '../rollout/Rollout.module.scss';
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanRunSheetProps {
  accountId: number;
  item: CatalogItem;
  release: GetReleaseKitResponse;
  /** `GET releases/:uuid/plan?kind=reels` of an active plan. */
  plan: ReleasePlan;
  reelsPlan: ReleaseReelsPlan;
  /** The usable channels (connected and manual). */
  channels: SerializedSocialChannel[];
  /** `YYYY-MM-DD` today in the time zone of the artist. */
  today: string;
  /** False for the demo artist and a read-only artist: the plan is read only. */
  canAct: boolean;
}

/**
 * The run sheet of an AI reels plan (design rollout-plan f3, f4, f6). It is the run sheet of the
 * rollout with the kind `reels`: the same step cards, step sheet and launch flow read the same
 * context. Each step holds its own reel; its drafts come when the reel is ready. "Schedule all N
 * posts" launches the drafts that exist now. "End the plan" asks first (`EndReelsPlanDialog`).
 */
const ReelsPlanRunSheetComponent: React.FC<ReelsPlanRunSheetProps> = ({
  accountId,
  item,
  release,
  plan,
  reelsPlan,
  channels,
  today,
  canAct,
}) => {
  const intl = useIntl();
  const isNarrow = useIsNarrowScreen();
  const { credits } = useVideoCredits({ accountId });
  const platforms = useMemo(() => channels.map((channel) => channel.platform as SocialPlatform), [channels]);
  const actions = useRolloutActions({ accountId, catalogItemUuid: item.uuid, platforms, kind: 'reels' });
  const { end, isEnding, endFeedback } = useReelsPlan({ accountId, catalogItemUuid: item.uuid });
  const [openStepKey, setOpenStepKey] = useState<string | null>(null);
  const [openPlatform, setOpenPlatform] = useState<string | null>(null);
  const [isEndOpen, setIsEndOpen] = useState(false);
  const openStep = useCallback((key: string, platform?: string) => {
    setOpenStepKey(key);
    setOpenPlatform(platform ?? null);
  }, []);
  const closeStep = useCallback(() => setOpenStepKey(null), []);
  const openEnd = useCallback(() => setIsEndOpen(true), []);
  const closeEnd = useCallback(() => setIsEndOpen(false), []);

  const steps = useMemo(() => planSteps(plan, today), [plan, today]);
  const drafts = useMemo(() => planDrafts(steps), [steps]);
  const endCounts = useMemo(() => reelsPlanEndCounts(steps), [steps]);
  const { data: quotaAnswer } = useGetPromoQuotaQuery({ accountId });
  const quota = quotaAnswer?.scheduledPost ?? null;
  const fits = useMemo(() => {
    const fit = fitDrafts({
      drafts: drafts.map((post) => ({ uuid: post.uuid, scheduledAt: post.scheduledAt })),
      limit: quota?.limit ?? null,
      used: quota?.used ?? 0,
      now: new Date(),
    });
    return new Map(fit.drafts.map((draft) => [draft.postUuid, draft]));
  }, [drafts, quota]);
  const quotaLine = useMemo(
    () =>
      quota === null || quota.limit === null
        ? null
        : { month: intl.formatDate(new Date(), { month: 'long' }), used: quota.used, limit: quota.limit },
    [intl, quota]
  );

  const { launch } = actions;
  // Every draft goes to the server: it schedules what can go out and names the rest.
  const scheduleAll = useCallback(() => {
    launch(
      'plan',
      drafts.map((post) => post.uuid)
    ).catch(console.error);
  }, [drafts, launch]);
  const confirmEnd = useCallback(() => {
    end()
      .then((ok) => (ok ? setIsEndOpen(false) : undefined))
      .catch(console.error);
  }, [end]);

  const value = useMemo<RolloutContextValue>(
    () => ({
      accountId,
      item,
      release,
      plan,
      today,
      timeZone: plan.timeZone ?? null,
      channels,
      actions,
      canAct,
      creditsLeft: credits?.totalLeft ?? null,
      fits,
      openStep,
      openStepKey,
    }),
    [accountId, actions, canAct, channels, credits?.totalLeft, fits, item, openStep, openStepKey, plan, release, today]
  );
  const openStepView = steps.find((step) => step.key === openStepKey) ?? null;

  return (
    <RolloutContext.Provider value={value}>
      <div className={rolloutStyles.sheet} data-testid="reels-plan-run">
        <LaunchSummary steps={steps} />
        <ScheduleAllUpsell drafts={drafts} quota={quota} />
        <Panel as="section" aria-label={intl.formatMessage(reelsPlanMessages.title)}>
          <ReelsPlanHead
            reelsPlan={reelsPlan}
            steps={steps}
            draftCount={drafts.length}
            isNarrow={isNarrow}
            onScheduleAll={scheduleAll}
            onEnd={openEnd}
          />
          <ReelsPlanChannelsLine steps={steps} quota={quotaLine} />
          <ReelsPlanSteps steps={steps} />
        </Panel>
        {isNarrow && drafts.length > 0 && (
          <div className={styles.sticky}>
            <SMLButton
              variant="primary"
              className={styles.wide}
              disabled={!canAct || actions.busy !== null}
              onClick={scheduleAll}
            >
              {intl.formatMessage(launchMessages.scheduleAll, { count: drafts.length })}
            </SMLButton>
          </div>
        )}
        <StepSheet step={openStepView} platform={openPlatform} onClose={closeStep} />
        <LaunchQuestionsDialog />
        <EndReelsPlanDialog
          isOpen={isEndOpen}
          drafts={endCounts.drafts}
          scheduled={endCounts.scheduled}
          reels={reelsPlan.reelCount}
          isBusy={isEnding}
          error={endFeedback?.tone === 'danger' ? endFeedback.message : undefined}
          onConfirm={confirmEnd}
          onCancel={closeEnd}
        />
      </div>
    </RolloutContext.Provider>
  );
};

export const ReelsPlanRunSheet = TypedMemo(ReelsPlanRunSheetComponent);
